/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

// The live `cart_attribute` value on this store's Tech-Air entry.
const TECH_AIR_ATTRIBUTE = 'has_tech_air_bag';

/**
 * Tech-Air's selection key mirrors the legacy `_current_tech_air_bag` name;
 * any other question gets a generic `_<attribute>_selection`.
 * @param {string} attribute
 */
function selectionKey(attribute) {
  return attribute === TECH_AIR_ATTRIBUTE ? '_current_tech_air_bag' : `_${attribute}_selection`;
}

/**
 * @param {Record<string, string>} attributes
 */
async function updateCartAttributes(attributes) {
  try {
    await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ attributes }) }));
  } catch {
    // Best effort — worst case the question re-appears on the next render.
  }
}

/**
 * Inline `cart_item_upsell` Q&A — rendered by `snippets/cart-products.liquid`
 * above the "You may also like" carousel in the cart drawer, one consolidated
 * block for the whole cart. Shows one pending question at a time and
 * advances through several locally, then shows a brief confirmation and
 * hides itself once every question is answered.
 */
class CartItemUpsellInlineComponent extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hideTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#activateFirstQuestion();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#hideTimer);
  }

  /** @param {Event} event */
  answerYes(event) {
    this.#answer(event, true);
  }

  /** @param {Event} event */
  answerNo(event) {
    this.#answer(event, false);
  }

  /**
   * @param {Event} event
   * @param {boolean} isYes
   */
  #answer(event, isYes) {
    const questionEl = this.#closestQuestion(event);
    if (!questionEl) return;

    const attribute = questionEl.dataset.attribute;
    const mode = isYes ? questionEl.dataset.yesMode : questionEl.dataset.noMode;
    if (!attribute) return;

    const prefix = isYes ? 'yes' : 'no';

    if (mode === 'dropdown') {
      // Don't save yet — a dropdown answer isn't complete until selected.
      this.#showStep(questionEl, `${prefix}-dropdown`);
      return;
    }

    updateCartAttributes({ [`_${attribute}`]: isYes ? 'true' : 'false', [`upsell-${attribute}`]: prefix });

    if (mode === 'product') {
      this.#showStep(questionEl, `${prefix}-product`);
    } else {
      this.#advance(questionEl);
    }
  }

  /** @param {Event} event */
  back(event) {
    const questionEl = this.#closestQuestion(event);
    if (!questionEl) return;
    this.#showStep(questionEl, 'question');
  }

  /** @param {Event} event */
  advance(event) {
    const questionEl = this.#closestQuestion(event);
    if (!questionEl) return;
    this.#advance(questionEl);
  }

  /** @param {Event} event */
  selectDropdown(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.value) return;

    const questionEl = select.closest('[data-question-index]');
    if (!(questionEl instanceof HTMLElement)) return;

    const attribute = select.dataset.attribute;
    if (!attribute) return;

    const isNo = select.dataset.answer === 'no';
    updateCartAttributes({
      [`_${attribute}`]: isNo ? 'false' : 'true',
      [selectionKey(attribute)]: select.value,
      [`upsell-${attribute}`]: select.value,
    });
    this.#advance(questionEl);
  }

  /**
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #closestQuestion(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    return target.closest('[data-question-index]');
  }

  #activateFirstQuestion() {
    const first = this.querySelector('[data-question-index="0"]');
    this.#activateQuestion(first instanceof HTMLElement ? first : null);
  }

  /** @param {HTMLElement | null} questionEl */
  #activateQuestion(questionEl) {
    for (const question of this.querySelectorAll('[data-question-index]')) {
      if (!(question instanceof HTMLElement)) continue;
      question.hidden = question !== questionEl;
    }
    if (questionEl) this.#showStep(questionEl, 'question');
  }

  /**
   * @param {HTMLElement} questionEl
   * @param {string} stepName
   */
  #showStep(questionEl, stepName) {
    for (const step of questionEl.querySelectorAll('[data-step-name]')) {
      if (!(step instanceof HTMLElement)) continue;
      step.hidden = step.dataset.stepName !== stepName;
    }
  }

  /** @param {HTMLElement} questionEl */
  #advance(questionEl) {
    const index = Number(questionEl.dataset.questionIndex ?? -1);
    const next = this.querySelector(`[data-question-index="${index + 1}"]`);

    if (next instanceof HTMLElement) {
      this.#activateQuestion(next);
      return;
    }

    for (const question of this.querySelectorAll('[data-question-index]')) {
      if (question instanceof HTMLElement) question.hidden = true;
    }

    const final = this.querySelector('[data-step-name="final"]');
    if (final instanceof HTMLElement) final.hidden = false;

    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => {
      this.hidden = true;
    }, 1600);
  }
}

if (!customElements.get('cart-item-upsell-inline-component')) {
  customElements.define('cart-item-upsell-inline-component', CartItemUpsellInlineComponent);
}

/**
 * Clears a question's saved answer when its recommended product's line
 * (tagged `_upsell_type` by `cart-upsell-form.js`) is removed from the cart,
 * so the inline question can ask again. One instance, always rendered
 * alongside the cart items list — its `data-tracked-attributes` is kept
 * current by the same section morph that already re-renders the cart on
 * every update.
 */
class CartItemUpsellTrackerComponent extends Component {
  /** @type {Set<string>} */
  #tracked = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#tracked = this.#parseTracked();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #parseTracked() {
    return new Set(
      (this.dataset.trackedAttributes || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    );
  }

  #handleCartUpdate = () => {
    // Deferred so the cart-wide morph (which refreshes this element's own
    // data-tracked-attributes from fresh HTML) settles first.
    requestAnimationFrame(() => {
      const next = this.#parseTracked();
      const removed = [...this.#tracked].filter((attribute) => !next.has(attribute));
      this.#tracked = next;

      if (removed.length === 0) return;

      /** @type {Record<string, string>} */
      const payload = {};
      for (const attribute of removed) {
        payload[`upsell-${attribute}`] = '';
        payload[`_${attribute}`] = '';
        payload[selectionKey(attribute)] = '';
      }
      updateCartAttributes(payload);
    });
  };
}

if (!customElements.get('cart-item-upsell-tracker-component')) {
  customElements.define('cart-item-upsell-tracker-component', CartItemUpsellTrackerComponent);
}
